/**
 * Deep links do assistente para telas do ERP.
 *
 * Só rotas desta lista (confirmadas em `component/layout/DefaultRouter.js`)
 * viram link clicável — qualquer outro href vindo do LLM (externo, `javascript:`,
 * rota inexistente) é descartado e o rótulo aparece como texto simples.
 * As permissões são as mesmas chaves do menu (`navConfig.js`); o chip só
 * aparece se o usuário puder abrir a tela.
 */

const ID = '[A-Za-z0-9-]{1,64}';

// padrão (pathname completo) → { label padrão, permissão }
const ROTAS = [
  // `permission` pode ser uma chave ou uma lista (qualquer uma libera), como no menu
  { re: /^\/sales$/, label: 'Ver vendas', permission: ['sale.view', 'home.view'] },
  { re: new RegExp(`^/sales/${ID}$`), label: 'Abrir venda', permission: ['sale.view', 'home.view'] },
  { re: /^\/reports\/sales$/, label: 'Abrir relatório de vendas', permission: 'report.sale' },
  { re: /^\/reports\/products$/, label: 'Abrir relatório de produtos', permission: 'report.product' },
  { re: /^\/reports\/stock$/, label: 'Abrir relatório de estoque', permission: 'report.stock' },
  { re: /^\/reports\/customers$/, label: 'Abrir relatório de clientes', permission: 'report.customer' },
  { re: /^\/reports\/cash-flow$/, label: 'Abrir fluxo de caixa', permission: ['report.cashflow', 'dashboard.sale'] },
  { re: /^\/dashboard\/sale$/, label: 'Abrir dashboard de vendas', permission: 'dashboard.sale' },
  { re: /^\/products$/, label: 'Abrir produtos', permission: 'product.view' },
  { re: /^\/customers$/, label: 'Abrir clientes', permission: 'customer.view' },
  { re: new RegExp(`^/customers/view/${ID}$`), label: 'Abrir cliente', permission: 'customer.view' },
  { re: /^\/suppliers$/, label: 'Abrir fornecedores', permission: 'supplier.view' },
  { re: /^\/cashregisters$/, label: 'Abrir caixas', permission: 'cashregister.view' },
  { re: /^\/transactions$/, label: 'Abrir lançamentos', permission: 'accounts.payable.view' },
  { re: /^\/serviceorders$/, label: 'Ver ordens de serviço', permission: 'serviceorder.view' },
  { re: new RegExp(`^/serviceorders/${ID}/view$`), label: 'Abrir ordem de serviço', permission: 'serviceorder.view' },
];

/**
 * Valida um href vindo do LLM. Devolve { path, label, permission } ou null.
 * Aceita só caminho interno absoluto; query e hash são descartados.
 */
export function resolverDeepLink(href) {
  if (typeof href !== 'string') return null;
  const h = href.trim();
  if (!h.startsWith('/') || h.startsWith('//') || h.includes('\\')) return null;
  const path = h.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  const rota = ROTAS.find((r) => r.re.test(path));
  return rota ? { path, label: rota.label, permission: rota.permission } : null;
}

/**
 * Sugestões de continuação por tool consultada (nomes do catálogo, JORNADA §3).
 * `links` são paths de ROTAS; `asks` são perguntas prontas (chips de follow-up).
 */
const POR_TOOL = {
  consultar_faturamento: { links: ['/sales', '/reports/sales'], asks: ['Compare com o período anterior'] },
  consultar_ticket_medio: { links: ['/reports/sales'], asks: ['Qual o ticket médio por vendedor?'] },
  consultar_margem: { links: ['/reports/products'], asks: ['Quais produtos têm a menor margem?'] },
  consultar_vendas: { links: ['/sales'], asks: ['Qual o faturamento desse período?'] },
  consultar_produtos_mais_vendidos: { links: ['/reports/products'], asks: ['Algum deles está com estoque baixo?'] },
  consultar_estoque_baixo: { links: ['/reports/stock', '/suppliers'], asks: ['Quais vendem mais?'] },
  listar_produtos: { links: ['/products'], asks: [] },
  detalhar_produto: { links: ['/products'], asks: [] },
  listar_clientes: { links: ['/customers'], asks: [] },
  resumo_cliente: { links: ['/customers'], asks: [] },
  consultar_clientes_inativos: { links: ['/customers'], asks: ['Quem mais comprou no último ano?'] },
  resumo_caixa: { links: ['/cashregisters'], asks: [] },
  consultar_contas_receber: { links: ['/transactions'], asks: ['Quais estão vencidas?'] },
  consultar_contas_pagar: { links: ['/transactions'], asks: ['Quais vencem esta semana?'] },
  consultar_ordens_servico: { links: ['/serviceorders'], asks: [] },
};

// Texto amigável do `tool_status` (o evento traz o nome técnico da tool).
const ROTULO_TOOL = {
  consultar_faturamento: 'Consultando faturamento…',
  consultar_ticket_medio: 'Calculando ticket médio…',
  consultar_margem: 'Calculando margem…',
  consultar_vendas: 'Consultando vendas…',
  consultar_produtos_mais_vendidos: 'Consultando produtos mais vendidos…',
  consultar_estoque_baixo: 'Consultando estoque…',
  listar_produtos: 'Consultando produtos…',
  detalhar_produto: 'Consultando produto…',
  listar_clientes: 'Consultando clientes…',
  resumo_cliente: 'Consultando cliente…',
  consultar_clientes_inativos: 'Consultando clientes…',
  resumo_caixa: 'Consultando caixa…',
  consultar_contas_receber: 'Consultando contas a receber…',
  consultar_contas_pagar: 'Consultando contas a pagar…',
  consultar_ordens_servico: 'Consultando ordens de serviço…',
};

export const rotuloDaTool = (tool) => ROTULO_TOOL[tool] || 'Consultando dados…';

/** Follow-ups para o conjunto de tools usadas numa resposta (sem repetição). */
export function sugestoesPorTools(tools) {
  const links = [];
  const asks = [];
  (tools || []).forEach((nome) => {
    const s = POR_TOOL[nome];
    if (!s) return;
    s.links.forEach((p) => { if (!links.includes(p)) links.push(p); });
    s.asks.forEach((a) => { if (!asks.includes(a)) asks.push(a); });
  });
  return {
    links: links.map(resolverDeepLink).filter(Boolean),
    asks: asks.slice(0, 2),
  };
}
